import { useState, useEffect } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertCircle, ArrowLeft, Edit2 } from 'lucide-react';
import { createFileRoute } from '@tanstack/react-router';
import SuperAdminLayout from '@/components/SuperAdminLayout';
import { apiFetch } from '@/utils/api';

interface Organization {
    id: string;
    name: string;
    slug: string;
    description: string | null;
    base_currency: string;
    is_active: boolean;
    created_at: string | null;
    user_count: number;
    // Module access flags
    module_people_enabled: boolean;
    module_inventory_enabled: boolean;
    module_menus_enabled: boolean;
    gdrive_invoice_processing_pipeline: 'current' | 'gemini_raw';
}

interface AdminUser {
    id: string;
    email: string;
    full_name: string | null;
    is_active: boolean;
    is_admin: boolean;
    last_login: string | null;
}

function TenantDetailPage() {
    const navigate = useNavigate();
    const { id } = useParams({ from: '/superadmin/tenants/$id' });
    const [organization, setOrganization] = useState<Organization | null>(null);

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const [editMode, setEditMode] = useState(false);
    const [editData, setEditData] = useState<Partial<Organization>>({});

    // Admin users state
    const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
    const [showNewAdminForm, setShowNewAdminForm] = useState(false);
    const [newAdminData, setNewAdminData] = useState({ email: '', full_name: '', password: '' });
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editAdminData, setEditAdminData] = useState<Partial<AdminUser> & { password?: string }>({});

    useEffect(() => {
        fetchTenantDetails();
        fetchAdminUsers();
    }, [id]);

    const fetchTenantDetails = async () => {
        try {
            const response = await apiFetch(`/api/superadmin/organizations/${id}`);

            if (!response.ok) {
                if (response.status === 401) {
                    navigate({ to: '/superadmin-login' });


                    return;
                }
                throw new Error('Failed to fetch tenant');
            }

            const data = await response.json();
            setOrganization(data);
            setEditData(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setIsLoading(false);
        }
    };

    // Admin users functions
    const fetchAdminUsers = async () => {
        try {
            const response = await apiFetch(`/api/superadmin/organizations/${id}/admins`);
            if (!response.ok) {
                if (response.status === 401) {
                    navigate({ to: '/superadmin-login' });
                    return;
                }
                throw new Error('Failed to fetch admin users');
            }
            const data = await response.json();
            setAdminUsers(data);
        } catch (err) {
            console.error('Failed to fetch admin users:', err);
        }
    };

    const handleCreateAdmin = async () => {
        try {
            const response = await apiFetch(`/api/superadmin/organizations/${id}/admins`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    email: newAdminData.email,
                    full_name: newAdminData.full_name || null,
                    password: newAdminData.password,
                    is_admin: true,
                }),
            });
            if (!response.ok) {
                const data = await response.json();
                setError(data?.message || (Array.isArray(data?.errors) ? data.errors.join('\n') : data?.detail) || 'Failed to create admin user');
                return;
            }
            setError('');

            setNewAdminData({ email: '', full_name: '', password: '' });
            setShowNewAdminForm(false);
            await fetchAdminUsers();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        }
    };

    const startEditAdmin = (user: AdminUser) => {
        setEditingId(user.id);
        setEditAdminData({
            email: user.email,
            full_name: user.full_name,
            is_active: user.is_active,
            is_admin: user.is_admin,
        });
    };

    const cancelEditAdmin = () => {
        setEditingId(null);
        setEditAdminData({});
    };

    const handleUpdateAdmin = async () => {
        if (!editingId) return;
        try {
            const response = await apiFetch(`/api/superadmin/organizations/${id}/admins/${editingId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(editAdminData),
            });
            if (!response.ok) {
                const data = await response.json();
                setError(data?.message || (Array.isArray(data?.errors) ? data.errors.join('\n') : data?.detail) || 'Failed to update admin user');
                return;
            }
            setError('');

            setEditingId(null);
            setEditAdminData({});
            await fetchAdminUsers();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        }
    };




    const handleSaveOrganization = async () => {
        try {
            const response = await apiFetch(`/api/superadmin/organizations/${id}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: editData.name,
                    description: editData.description,
                    base_currency: editData.base_currency,
                    is_active: editData.is_active,
                    gdrive_invoice_processing_pipeline: editData.gdrive_invoice_processing_pipeline,
                }),
            });

            if (!response.ok) {
                const data = await response.json();
                setError(data?.message || (Array.isArray(data?.errors) ? data.errors.join('\n') : data?.detail) || 'Failed to update tenant');
                return;
            }

            const data = await response.json();
            setOrganization(data);
            setError('');

            setEditMode(false);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
        }
    };





    if (isLoading) {
        return <div className="text-center py-12">Loading...</div>;
    }

    if (!organization) {
        return <div className="text-center py-12">Tenant not found</div>;
    }

    return (
        <SuperAdminLayout>
            <div className="space-y-6">
                <div className="flex items-center gap-4">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={() => navigate({ to: '/superadmin/tenants' })}
                        className="gap-2"
                    >
                        <ArrowLeft className="h-4 w-4" />
                        Back
                    </Button>
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">{organization.name}</h1>
                        <p className="text-slate-600 mt-1">Manage tenant settings</p>
                    </div>
                </div>

                {error && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription className="whitespace-pre-line">{error}</AlertDescription>
                    </Alert>
                )}

                <div className="bg-white rounded-lg border border-slate-200 p-6 max-w-2xl">
                    {editMode ? (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-slate-900 mb-2">
                                    Name
                                </label>
                                <Input
                                    value={editData.name || ''}
                                    onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-900 mb-2">
                                    Description
                                </label>
                                <textarea
                                    value={editData.description || ''}
                                    onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-900 mb-2">
                                    Base Currency
                                </label>
                                <select
                                    id="base_currency"
                                    value={editData.base_currency || ''}
                                    onChange={(e) => setEditData({ ...editData, base_currency: (e.target as HTMLSelectElement).value })}
                                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                                >
                                    <option value="HUF">HUF - Hungarian Forint</option>
                                    <option value="EUR">EUR - Euro</option>
                                    <option value="USD">USD - US Dollar</option>
                                    <option value="GBP">GBP - British Pound</option>
                                    <option value="CHF">CHF - Swiss Franc</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-slate-900 mb-2">
                                    Google Drive Invoice Processing Pipeline
                                </label>
                                <select
                                    id="gdrive_invoice_processing_pipeline"
                                    value={editData.gdrive_invoice_processing_pipeline || 'current'}
                                    onChange={(e) =>
                                        setEditData({
                                            ...editData,
                                            gdrive_invoice_processing_pipeline:
                                                (e.target as HTMLSelectElement).value as 'current' | 'gemini_raw',
                                        })
                                    }
                                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm"
                                >
                                    <option value="current">Current pipeline (existing OCR + AI flow)</option>
                                    <option value="gemini_raw">Gemini raw (direct Gemini, no pre/post processing)</option>
                                </select>
                                <p className="text-xs text-slate-600 mt-1">
                                    Applies to Google Drive invoice extraction for this tenant.
                                </p>
                            </div>
                            <div className="flex gap-2">
                                <Button onClick={handleSaveOrganization}>Save</Button>
                                <Button variant="outline" onClick={() => setEditMode(false)}>Cancel</Button>
                            </div>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            <div>
                                <p className="text-sm text-slate-600">Name</p>
                                <p className="text-lg font-medium">{organization.name}</p>
                            </div>
                            <div>
                                <p className="text-sm text-slate-600">Slug</p>
                                <p className="text-lg font-medium">{organization.slug}</p>
                            </div>
                            <div>
                                <p className="text-sm text-slate-600">Description</p>
                                <p className="text-lg font-medium">{organization.description || '-'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-slate-600">Base Currency</p>
                                <p className="text-lg font-medium">{organization.base_currency}</p>
                            </div>
                            <div>
                                <p className="text-sm text-slate-600">Status</p>
                                <p className="text-lg font-medium">{organization.is_active ? 'Active' : 'Inactive'}</p>
                            </div>
                            <div>
                                <p className="text-sm text-slate-600">Google Drive Invoice Processing Pipeline</p>
                                <p className="text-lg font-medium">
                                    {organization.gdrive_invoice_processing_pipeline === 'gemini_raw'
                                        ? 'Gemini raw'
                                        : 'Current pipeline'}
                                </p>
                            </div>
                            <Button onClick={() => setEditMode(true)} className="gap-2">
                                <Edit2 className="h-4 w-4" />
                                Edit
                            </Button>
                        </div>
                    )}
                </div>

                {/* Module Access Settings Card */}
                <div className="bg-white rounded-lg border border-slate-200 p-6 max-w-2xl">
                    <div className="mb-4">
                        <h2 className="text-xl font-semibold">Module Access</h2>
                        <p className="text-sm text-slate-600">Enable or disable optional modules for this tenant</p>
                    </div>

                    <div className="space-y-4">
                        <label className="flex items-center gap-3 cursor-not-allowed">
                            <input
                                type="checkbox"
                                checked
                                disabled
                                className="h-4 w-4 rounded border-slate-300"
                            />
                            <div>
                                <span className="text-sm font-medium text-slate-900">Finance</span>
                                <p className="text-xs text-slate-500">Expenses, revenues, transactions, inbox (always enabled)</p>
                            </div>
                        </label>

                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={organization.module_people_enabled}
                                onChange={async (e) => {
                                    const newValue = e.target.checked;
                                    try {
                                        const response = await apiFetch(`/api/superadmin/organizations/${id}`, {
                                            method: 'PATCH',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({ module_people_enabled: newValue }),
                                        });
                                        if (response.ok) {
                                            const data = await response.json();
                                            setOrganization(data);
                                        } else {
                                            setError('Failed to update module settings');
                                        }
                                    } catch (err) {
                                        setError('Failed to update module settings');
                                    }
                                }}
                                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div>
                                <span className="text-sm font-medium text-slate-900">People & HR</span>
                                <p className="text-xs text-slate-500">Employees, timesheets, time off, people costs</p>
                            </div>
                        </label>

                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={organization.module_inventory_enabled}
                                onChange={async (e) => {
                                    const newValue = e.target.checked;
                                    try {
                                        const response = await apiFetch(`/api/superadmin/organizations/${id}`, {
                                            method: 'PATCH',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({ module_inventory_enabled: newValue }),
                                        });
                                        if (response.ok) {
                                            const data = await response.json();
                                            setOrganization(data);
                                        } else {
                                            setError('Failed to update module settings');
                                        }
                                    } catch (err) {
                                        setError('Failed to update module settings');
                                    }
                                }}
                                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div>
                                <span className="text-sm font-medium text-slate-900">Inventory</span>
                                <p className="text-xs text-slate-500">Stock management, batches, ingredients, transactions</p>
                            </div>
                        </label>

                        <label className="flex items-center gap-3 cursor-pointer">
                            <input
                                type="checkbox"
                                checked={organization.module_menus_enabled}
                                onChange={async (e) => {
                                    const newValue = e.target.checked;
                                    try {
                                        const response = await apiFetch(`/api/superadmin/organizations/${id}`, {
                                            method: 'PATCH',
                                            headers: { 'Content-Type': 'application/json' },
                                            body: JSON.stringify({ module_menus_enabled: newValue }),
                                        });
                                        if (response.ok) {
                                            const data = await response.json();
                                            setOrganization(data);
                                        } else {
                                            setError('Failed to update module settings');
                                        }
                                    } catch (err) {
                                        setError('Failed to update module settings');
                                    }
                                }}
                                className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                            />
                            <div>
                                <span className="text-sm font-medium text-slate-900">Menus</span>
                                <p className="text-xs text-slate-500">Menu items and recipes</p>
                            </div>
                        </label>
                    </div>
                </div>

                {/* Admin Users Card */}
                <div className="bg-white rounded-lg border border-slate-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h2 className="text-xl font-semibold">Admin users</h2>
                            <p className="text-sm text-slate-600">Create and edit admin users for this tenant</p>
                        </div>
                        {!showNewAdminForm && (
                            <Button onClick={() => setShowNewAdminForm(true)}>New admin</Button>
                        )}
                    </div>

                    {showNewAdminForm && (
                        <div className="mb-6 space-y-3">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                <Input
                                    placeholder="Email"
                                    value={newAdminData.email}
                                    onChange={(e) => setNewAdminData({ ...newAdminData, email: e.target.value })}
                                />
                                <Input
                                    placeholder="Full name (optional)"
                                    value={newAdminData.full_name}
                                    onChange={(e) => setNewAdminData({ ...newAdminData, full_name: e.target.value })}
                                />
                                <Input
                                    placeholder="Password"
                                    type="password"
                                    value={newAdminData.password}
                                    onChange={(e) => setNewAdminData({ ...newAdminData, password: e.target.value })}
                                />
                            </div>
                            <div className="flex gap-2">
                                <Button onClick={handleCreateAdmin}>Create</Button>
                                <Button variant="outline" onClick={() => { setShowNewAdminForm(false); setNewAdminData({ email: '', full_name: '', password: '' }); }}>Cancel</Button>
                            </div>
                        </div>
                    )}

                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="text-left text-slate-600 border-b">
                                    <th className="py-2 pr-4">Email</th>
                                    <th className="py-2 pr-4">Full name</th>
                                    <th className="py-2 pr-4">Status</th>
                                    <th className="py-2 pr-4">Last login</th>
                                    <th className="py-2 pr-4"></th>
                                </tr>
                            </thead>
                            <tbody>
                                {adminUsers.map((u) => (
                                    <tr key={u.id} className="border-b">
                                        {editingId === u.id ? (
                                            <>
                                                <td className="py-2 pr-4">
                                                    <Input
                                                        value={editAdminData.email || ''}
                                                        onChange={(e) => setEditAdminData({ ...editAdminData, email: e.target.value })}
                                                    />
                                                </td>
                                                <td className="py-2 pr-4">
                                                    <Input
                                                        value={editAdminData.full_name || ''}
                                                        onChange={(e) => setEditAdminData({ ...editAdminData, full_name: e.target.value })}
                                                    />
                                                </td>
                                                <td className="py-2 pr-4">
                                                    <label className="inline-flex items-center gap-2 text-slate-700">
                                                        <input
                                                            type="checkbox"
                                                            className="h-4 w-4"
                                                            checked={!!editAdminData.is_active}
                                                            onChange={(e) => setEditAdminData({ ...editAdminData, is_active: e.target.checked })}
                                                        />
                                                        Active
                                                    </label>
                                                    <label className="inline-flex items-center gap-2 text-slate-700 ml-4">
                                                        <input
                                                            type="checkbox"
                                                            className="h-4 w-4"
                                                            checked={!!editAdminData.is_admin}
                                                            onChange={(e) => setEditAdminData({ ...editAdminData, is_admin: e.target.checked })}
                                                        />
                                                        Admin
                                                    </label>
                                                </td>
                                                <td className="py-2 pr-4">
                                                    <Input
                                                        type="password"
                                                        placeholder="New password (optional)"
                                                        value={editAdminData.password || ''}
                                                        onChange={(e) => setEditAdminData({ ...editAdminData, password: e.target.value })}
                                                    />
                                                </td>
                                                <td className="py-2 pr-4 whitespace-nowrap">
                                                    <div className="flex gap-2">
                                                        <Button size="sm" onClick={handleUpdateAdmin}>Save</Button>
                                                        <Button size="sm" variant="outline" onClick={cancelEditAdmin}>Cancel</Button>
                                                    </div>
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td className="py-2 pr-4">{u.email}</td>
                                                <td className="py-2 pr-4">{u.full_name || '-'}</td>
                                                <td className="py-2 pr-4">
                                                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${u.is_active ? 'bg-green-100 text-green-800' : 'bg-slate-200 text-slate-700'}`}>
                                                        {u.is_active ? 'Active' : 'Inactive'}
                                                    </span>
                                                </td>
                                                <td className="py-2 pr-4">{u.last_login ? new Date(u.last_login).toLocaleString() : '-'}</td>
                                                <td className="py-2 pr-4">
                                                    <Button size="sm" variant="outline" onClick={() => startEditAdmin(u)}>Edit</Button>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                                {adminUsers.length === 0 && (
                                    <tr>
                                        <td className="py-6 text-center text-slate-500" colSpan={5}>No admin users yet</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

            </div>
        </SuperAdminLayout>
    );
}

export const Route = createFileRoute('/superadmin/tenants/$id')({
    component: TenantDetailPage,
});
